import { API_URL } from "../config";
import { onAuthenticationError, store } from "../redux";

export type MakeRequestError = {
  error: string;
};

export type MakeRequestOptions = {
  method?: string;
  headers?: Record<string, string>;
  body?: Record<string, unknown>;
  file?: File;
  files?: FileList;
  blob?: boolean;
};

export const makeRequest = async <T>(url: string, options?: MakeRequestOptions): Promise<T> => {
  let headers = options?.headers || {};
  let bodyFormData = new FormData();
  let body: FormData | string | undefined = undefined;

  // Add Organization-Id header if admin has selected a different organization
  const state = store.getState();
  const { user, selectedOrganizationId } = state.session;
  if (user?.isAdmin && selectedOrganizationId && selectedOrganizationId !== user.organizationId) {
    headers["Organization-Id"] = selectedOrganizationId;
  }

  if (options?.file || options?.files) {
    // Use FormData for file uploads
    body = bodyFormData;

    if (options?.file) {
      bodyFormData.append("file", options.file);
    }

    if (options?.files) {
      for (let i = 0; i < options.files.length; i++) {
        bodyFormData.append("file", options.files[i]);
      }
    }

    // Append body data to FormData if both file and body are provided
    if (options?.body) {
      Object.entries(options.body).forEach(([key, value]) => {
        bodyFormData.append(key, String(value));
      });
    }
  } else if (options?.body) {
    // Use JSON for body-only requests
    headers["Content-Type"] = "application/json";
    body = JSON.stringify(options?.body);
  }

  const response = await fetch(`${API_URL}${url}`, {
    method: options?.method || "GET",
    credentials: "include",
    headers,
    body,
  });

  if (response.status === 204) {
    return null as T;
  }

  if (response.status === 401 && url !== "/v1/auth/session") {
    onAuthenticationError();
  }

  if (options?.blob) {
    return response.blob() as T;
  }

  const json = await response.json();
  if (response.ok) {
    return json as T;
  }

  const error = json as MakeRequestError;

  throw new Error(error.error);
};

export const makeStreamingRequest = async (url: string, options?: MakeRequestOptions): Promise<Response> => {
  let headers = options?.headers || {};
  let bodyFormData = new FormData();
  let body: FormData | string | undefined = undefined;

  if (options?.body) {
    headers["Content-Type"] = "application/json";
    body = JSON.stringify(options?.body);
  }

  if (options?.file) {
    body = bodyFormData;
    bodyFormData.append("file", options.file);
  }

  if (options?.files) {
    body = bodyFormData;
    for (let i = 0; i < options.files.length; i++) {
      bodyFormData.append("file", options.files[i]);
    }
  }

  const response = await fetch(`${API_URL}${url}`, {
    method: options?.method || "GET",
    credentials: "include",
    headers,
    body,
  });

  if (response.status === 204) {
    throw new Error("No content");
  }

  if (response.status === 401 && url !== "/v1/auth/session") {
    onAuthenticationError();
  }

  if (!response.ok) {
    const error = (await response.json()) as MakeRequestError;
    throw new Error(error.error);
  }

  return response;
};
